"use client";

import type { EditableIndividualWithIdentity } from "@/lib/cat/individual-profile.types";
import { validateIndividualIdentity } from "@/lib/cat/party-profile-validation";
import {
  PartyProfileFields,
  ProfileFieldLabel,
  ProfileTextInput,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldContent, FieldError } from "@/components/ui/field";

function IndividualEditForm({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: EditableIndividualWithIdentity;
  onChange: (value: EditableIndividualWithIdentity) => void;
}) {
  const patch = (partial: Partial<EditableIndividualWithIdentity>) =>
    onChange({ ...value, ...partial });

  const identityErrors = validateIndividualIdentity(value);

  return (
    <Card className="border-muted">
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm font-medium">Compléter la fiche particulier</p>
        <p className="text-muted-foreground text-sm">
          Les champs marqués d&apos;un{" "}
          <span className="text-destructive">*</span> sont obligatoires pour
          passer à l&apos;étape suivante.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field data-invalid={identityErrors.lastName ? true : undefined}>
            <ProfileFieldLabel
              htmlFor={`${idPrefix}-lastName`}
              label="Nom"
              required
            />
            <FieldContent>
              <ProfileTextInput
                id={`${idPrefix}-lastName`}
                locked={false}
                invalid={Boolean(identityErrors.lastName)}
                value={value.lastName}
                onChange={(lastName) => patch({ lastName })}
              />
              {identityErrors.lastName ? (
                <FieldError>{identityErrors.lastName}</FieldError>
              ) : null}
            </FieldContent>
          </Field>
          <Field data-invalid={identityErrors.firstName ? true : undefined}>
            <ProfileFieldLabel
              htmlFor={`${idPrefix}-firstName`}
              label="Prénom"
            />
            <FieldContent>
              <ProfileTextInput
                id={`${idPrefix}-firstName`}
                locked={false}
                invalid={Boolean(identityErrors.firstName)}
                value={value.firstName}
                onChange={(firstName) => patch({ firstName })}
              />
              {identityErrors.firstName ? (
                <FieldError>{identityErrors.firstName}</FieldError>
              ) : null}
            </FieldContent>
          </Field>
        </div>

        <PartyProfileFields
          idPrefix={`${idPrefix}-party`}
          value={value}
          onChange={(party) => onChange({ ...value, ...party })}
          showValidation
          requireEmailOrPhone
        />
      </CardContent>
    </Card>
  );
}

export { IndividualEditForm };
